import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  getCampaignTimeline,
  skipOccurrence,
  sendOccurrenceNow,
  overrideOccurrence,
  getCampaignRun,
  resumeCampaignRun,
} from '../../Services/arndAPI';
import ConfirmModal from '../../Components/ConfirmModal/ConfirmModal';
import MessageEditor from './campaign/MessageEditor';
import { STAGE_LABELS, CHANNEL_ICON } from './campaign/placeholders';
import { fmtCT } from './campaign/time';
import { campaignSmsContext } from './campaignLength';
import styles from './Styles/EventOps.module.css';

const POLL_MS = 30000;

const STATUS_LABEL = { done: 'sent', failed: 'failed', missed: 'missed', stopped: 'stopped', running: 'sending…' };
const STATUS_CLASS = { done: 'runDone', failed: 'runFailed', missed: 'runMissed', stopped: 'runStopped', running: 'runRunning' };

const sentCounts = (counts) =>
  ['push', 'sms', 'email'].map((ch) => `${CHANNEL_ICON[ch]}${counts?.[ch]?.sent ?? 0}`).join(' ');

const isLive = (occ) => !occ.skipped && !occ.supersededBy;

/** A run that ended early can be picked up where it stopped. */
const RESUMABLE = new Set(['stopped', 'failed']);

/**
 * SCRUM-449: Event Ops "Campaigns" — one chronological list replacing Send
 * history. Past runs (auto and custom) oldest → newest, a "now" line, then
 * the upcoming automatic occurrences by due time, each with Edit / Skip /
 * Send now. Polls every 30 s whether open or not: the header's Auto: ON/OFF
 * and the page's off banner (`onLoaded`) depend on it.
 */
// The next step shows ONE example message (Imad 2026-09-26): the text a
// parent with the most data would get — the SMS when the campaign texts,
// else whatever it sends. The API still returns fullest/middle/sparsest
// per channel; the operator doesn't need all six.
export const exampleSample = (sample) => {
  const list = sample ?? [];
  return (
    list.find((s) => s.channel === 'sms' && s.label === 'fullest') ??
    list.find((s) => s.channel === 'sms') ??
    list.find((s) => s.label === 'fullest') ??
    list[0] ??
    null
  );
};

export default function CampaignTimeline({ eventId, eventName = '', eventSlug = null, refreshKey = 0, open, onToggle, onLoaded }) {
  const [timeline, setTimeline] = useState(null);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [expandedRun, setExpandedRun] = useState(null); // { id, rows|null, error }
  const [editing, setEditing] = useState(null); // { id, draft, invalid, saving, error }
  const [confirmSend, setConfirmSend] = useState(null); // { id, name, audience }
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  const load = useCallback(async () => {
    try {
      const data = await getCampaignTimeline(eventId);
      setTimeline(data);
      setError('');
      onLoadedRef.current?.(data);
    } catch (e) {
      setError(e.message);
    }
  }, [eventId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  useEffect(() => {
    const t = setInterval(() => {
      if (!document.hidden) load();
    }, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  const act = async (fn, done) => {
    setStatus('');
    try {
      await fn();
      if (done) setStatus(done);
    } catch (e) {
      setStatus(e.message);
    }
    await load();
  };

  // The editor stays open until the override is saved; an API refusal (a
  // 400 naming what is wrong) shows inside it and the text is kept.
  const saveOverride = async (occ, content, done) => {
    setEditing((cur) => (cur?.id === occ.id ? { ...cur, saving: true, error: '' } : cur));
    setStatus('');
    try {
      await overrideOccurrence(eventId, occ.id, content);
    } catch (e) {
      setEditing((cur) => (cur?.id === occ.id ? { ...cur, saving: false, error: e.message } : cur));
      return;
    }
    setEditing((cur) => (cur?.id === occ.id ? null : cur));
    setStatus(done);
    await load();
  };

  const toggleRun = async (run) => {
    if (expandedRun?.id === run.id) {
      setExpandedRun(null);
      return;
    }
    setExpandedRun({ id: run.id, rows: null, error: '' });
    try {
      const data = await getCampaignRun(eventId, run.id);
      setExpandedRun((cur) => (cur?.id === run.id ? { id: run.id, rows: data.rows ?? [], error: '' } : cur));
    } catch (e) {
      setExpandedRun((cur) => (cur?.id === run.id ? { id: run.id, rows: [], error: e.message } : cur));
    }
  };

  const auto = timeline?.event?.autoCampaigns;
  const replies = timeline?.replies ?? 0;
  const runs = [...(timeline?.runs ?? [])].reverse();
  const upcoming = [...(timeline?.upcoming ?? [])].sort((a, b) => new Date(a.dueAt) - new Date(b.dueAt));
  const nextLive = upcoming.find(isLive);
  // Not expired, not a test event — a missed step can still be sent.
  const eligible = !!timeline?.event?.eligible;
  // SCRUM-449: an override is sized on this event's real name and link —
  // the same context the API's save check uses.
  const smsEvent = { name: eventName || timeline?.event?.name || '', slug: eventSlug };

  return (
    <div className={styles.campaign}>
      <button className={styles.campaignHeader} onClick={onToggle}>
        <span>
          Campaigns — Auto: {timeline ? (auto ? 'ON' : 'OFF') : '…'}
          {replies > 0 && <span className={styles.timelineReplies}> · replies ({replies})</span>}
        </span>
        <span className={styles.campaignChevron}>{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className={styles.campaignBody}>
          {error && <div className={styles.smsWarn}>{error}</div>}
          {status && <div className={styles.subtle}>{status}</div>}
          <ul className={styles.timeline}>
            {runs.map((run) => (
              <li key={run.id} data-testid="timeline-run" className={styles.timelineRow}>
                <div className={styles.timelineLine}>
                  <button type="button" className={styles.timelineRunBtn} onClick={() => toggleRun(run)} title="Show recipients">
                    <span className={styles.timelineTime}>{fmtCT(run.createdAt)}</span>
                    <span className={run.kind === 'auto' ? styles.badgeAuto : styles.badgeCustom}>
                      {run.kind === 'auto' ? 'AUTO' : 'CUSTOM'}
                    </span>
                    <span className={styles.timelineName}>{run.label}</span>
                    <span className={styles.timelineCounts} title="Sent per channel">{sentCounts(run.counts)}</span>
                  </button>
                  <span
                    className={`${styles.runChip} ${styles[STATUS_CLASS[run.status]] ?? ''}`}
                    title={run.reason || undefined}
                  >
                    {STATUS_LABEL[run.status] ?? run.status}
                  </span>
                  {RESUMABLE.has(run.status) && (
                    <button
                      type="button"
                      className={styles.timelineAction}
                      onClick={() => act(() => resumeCampaignRun(eventId, run.id), `Resumed '${run.label}'`)}
                    >
                      Resume
                    </button>
                  )}
                  {run.status === 'missed' && run.occurrenceId && eligible && (
                    <button
                      type="button"
                      className={styles.timelineAction}
                      onClick={() => setConfirmSend({ id: run.occurrenceId, name: run.label, audience: null })}
                    >
                      Send now
                    </button>
                  )}
                </div>
                {expandedRun?.id === run.id && (
                  <div className={styles.timelineDetail}>
                    {expandedRun.error && <div className={styles.smsWarn}>{expandedRun.error}</div>}
                    {!expandedRun.rows ? (
                      <span className={styles.subtle}>Loading recipients…</span>
                    ) : expandedRun.rows.length === 0 ? (
                      <span className={styles.subtle}>No recipients.</span>
                    ) : (
                      <table className={styles.campaignTable}>
                        <thead>
                          <tr><th>Parent</th><th>Channel</th><th>Status</th><th>Time</th></tr>
                        </thead>
                        <tbody>
                          {expandedRun.rows.map((r, i) => (
                            <tr key={r.id ?? i}>
                              <td>{r.parentName}</td>
                              <td>{CHANNEL_ICON[r.channel] ?? r.channel}</td>
                              <td title={r.error || undefined}>{r.status}{r.error ? ` — ${r.error}` : ''}</td>
                              <td>{fmtCT(r.createdAt)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </li>
            ))}

            <li data-testid="timeline-now" className={styles.timelineNow}>
              <span>now</span>
            </li>

            {upcoming.map((occ) => {
              const greyed = !isLive(occ);
              return (
                <li
                  key={occ.id}
                  data-testid="timeline-upcoming"
                  className={`${styles.timelineRow} ${greyed ? styles.timelineGreyed : ''}`}
                >
                  <div className={styles.timelineLine}>
                    <span className={styles.timelineTime}>{fmtCT(occ.dueAt)}</span>
                    <span className={styles.timelineName} title={occ.objective || undefined}>{occ.name}</span>
                    {occ.overridden && <span className={styles.runChip}>edited</span>}
                    {(occ.stages ?? []).map((s) => (
                      <span key={s} className={styles.timelineStage}>{STAGE_LABELS[s] ?? s}</span>
                    ))}
                    <span className={styles.subtle}>~{occ.audience ?? 0} parents</span>
                    {occ.supersededBy && <span className={styles.subtle}>superseded by {occ.supersededBy.name}</span>}
                    {occ.skipped && <span className={styles.subtle}>skipped</span>}
                    <span className={styles.timelineActions}>
                      <button
                        type="button"
                        className={styles.timelineAction}
                        disabled={greyed}
                        title={greyed ? (occ.skipped ? 'Unskip it first' : 'Superseded — it will not send') : undefined}
                        onClick={() =>
                          setEditing(editing?.id === occ.id ? null : { id: occ.id, draft: { bodies: {}, ...occ.content } })
                        }
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className={styles.timelineAction}
                        disabled={!!occ.supersededBy}
                        title={occ.supersededBy ? 'Superseded — it will not send' : undefined}
                        onClick={() => act(() => skipOccurrence(eventId, occ.id, !occ.skipped))}
                      >
                        {occ.skipped ? 'Unskip' : 'Skip'}
                      </button>
                      <button
                        type="button"
                        className={styles.timelineAction}
                        disabled={greyed}
                        title={greyed ? (occ.skipped ? 'Unskip it first' : 'Superseded — it will not send') : undefined}
                        onClick={() => setConfirmSend({ id: occ.id, name: occ.name, audience: occ.audience ?? 0 })}
                      >
                        Send now
                      </button>
                    </span>
                  </div>
                  {occ === nextLive && exampleSample(occ.sample) && (
                    <div className={styles.timelineSamples}>
                      Example: {exampleSample(occ.sample).body}
                    </div>
                  )}
                  {editing?.id === occ.id && (
                    <div className={styles.timelineDetail}>
                      <MessageEditor
                        channels={occ.channels ?? []}
                        title={editing.draft.title ?? ''}
                        subject={editing.draft.subject ?? ''}
                        bodies={editing.draft.bodies ?? {}}
                        onChange={(draft) => setEditing((cur) => (cur?.id === occ.id ? { ...cur, draft } : cur))}
                        // SCRUM-434: size SMS to what is really sent — this event's
                        // name and link (the generic link for a lead campaign).
                        lengthContext={campaignSmsContext(occ.stages ?? [], smsEvent)}
                        // SCRUM-449: an SMS past one text cannot be saved.
                        onValidityChange={(invalid) =>
                          setEditing((cur) => (cur?.id === occ.id && cur.invalid !== invalid ? { ...cur, invalid } : cur))
                        }
                      />
                      {editing.error && <div className={styles.smsWarn} role="alert">{editing.error}</div>}
                      <div className={styles.timelineActions}>
                        <button
                          type="button"
                          className={styles.timelineAction}
                          disabled={!!editing.invalid || !!editing.saving}
                          onClick={() => saveOverride(occ, editing.draft, `Saved '${occ.name}' for this event`)}
                        >
                          Save for this event
                        </button>
                        <button
                          type="button"
                          className={styles.timelineAction}
                          disabled={!!editing.saving}
                          onClick={() => saveOverride(occ, null, `'${occ.name}' reset to the playbook`)}
                        >
                          Reset to playbook
                        </button>
                        <button type="button" className={styles.timelineAction} onClick={() => setEditing(null)}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
            {timeline && upcoming.length === 0 && (
              <li className={styles.subtle}>No automatic steps ahead.</li>
            )}
          </ul>
        </div>
      )}
      {confirmSend && (
        <ConfirmModal
          message={
            confirmSend.audience == null
              ? `Send '${confirmSend.name}' now?`
              : `Send '${confirmSend.name}' to ~${confirmSend.audience} parents now?`
          }
          onConfirm={() => {
            const occ = confirmSend;
            setConfirmSend(null);
            act(() => sendOccurrenceNow(eventId, occ.id), `Sending '${occ.name}' now`);
          }}
          onCancel={() => setConfirmSend(null)}
        />
      )}
    </div>
  );
}
